import {
  Users,
  DollarSign,
  Building2,
  UserCheck,
  AlertCircle,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import type {
  FundInvestorStatistics,
  InvestorStatistics,
} from '@/types/investor';
import { formatCurrency } from '@/utils/formatters';

interface InvestorStatisticsCardsProps {
  statistics: InvestorStatistics | FundInvestorStatistics | null | undefined;
  isFundView?: boolean;
}

interface StatItem {
  icon: LucideIcon;
  label: string;
  value: string | number;
}

export function InvestorStatisticsCards({
  statistics,
  isFundView,
}: InvestorStatisticsCardsProps) {
  if (!statistics) return null;

  return (
    <div className="grid grid-cols-2 gap-4 sm:flex sm:items-center sm:gap-8 py-2">
      {statItems(statistics, isFundView).map((item, index, items) => (
        <div key={item.label} className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <item.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {item.label}
              </span>
              <span className="text-lg font-semibold text-foreground truncate">
                {item.value}
              </span>
            </div>
          </div>
          {index < items.length - 1 && (
            <div className="hidden sm:block h-8 w-px bg-border flex-shrink-0" />
          )}
        </div>
      ))}
    </div>
  );
}

function statItems(
  statistics: InvestorStatistics | FundInvestorStatistics,
  isFundView?: boolean
): StatItem[] {
  if (!isFundView) {
    const stats = statistics as InvestorStatistics;
    return [
      { icon: Users, label: 'Total Investors', value: stats.total_investors },
      {
        icon: UserCheck,
        label: 'Verified KYC',
        value: stats.verified_kyc_count,
      },
      { icon: Building2, label: 'Corporate', value: stats.corporate_count },
      {
        icon: AlertCircle,
        label: 'Pending KYC',
        value: stats.pending_kyc_count,
      },
    ];
  }

  const stats = statistics as FundInvestorStatistics;
  const currency = { currency: stats.commitment_currency };
  return [
    { icon: Users, label: 'Total Investors', value: stats.total_investors },
    {
      icon: DollarSign,
      label: 'Total Commitments',
      value: formatCurrency(stats.total_commitments, currency),
    },
    {
      icon: TrendingUp,
      label: 'Total Contributed',
      value: formatCurrency(stats.total_contributed, currency),
    },
    { icon: Users, label: 'Active Investors', value: stats.active_investors },
  ];
}
